import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Divider,
  Alert,
} from "@mui/material";
import dayjs from "dayjs";

import { getActivityGroupsByDate, copyActivityGroups } from "../api/activitiesApi";

// Identify "the same activity on the same day" so a group already present on the
// target date isn't copied again. Keyed on campus + slot + product (or, for
// bespoke groups with no product, the normalised name) — deliberately NOT on
// name uniqueness alone, since two same-named groups can be legitimate.
function normaliseName(name) {
  return String(name || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function groupSignature(group) {
  const campus = String(group?.campus || "").trim().toLowerCase();
  const slot = String(group?.slot || "").trim().toUpperCase();
  const key =
    group?.productId != null
      ? `p:${group.productId}`
      : `n:${normaliseName(group?.name)}`;
  return `${campus}__${slot}__${key}`;
}

// Pull activity groups from a chosen source day into `targetDate` (the day the
// board is showing). The leader and member assignments are copied only when the
// matching checkbox is ticked.
export default function CopyGroupsDialog({ open, onClose, targetDate, onCopied }) {
  const defaultSourceDate = useMemo(
    () =>
      targetDate ? dayjs(targetDate).subtract(1, "day").format("YYYY-MM-DD") : "",
    [targetDate]
  );

  const targetDateStr = useMemo(
    () => (targetDate ? dayjs(targetDate).format("YYYY-MM-DD") : ""),
    [targetDate]
  );

  const [sourceDate, setSourceDate] = useState(defaultSourceDate);
  const [groups, setGroups] = useState([]);
  const [targetGroups, setTargetGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [includeLeader, setIncludeLeader] = useState(true);
  const [includeMembers, setIncludeMembers] = useState(false);
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState(null);

  // Re-seed the source date and clear any prior result each time the dialog opens.
  useEffect(() => {
    if (open) {
      setSourceDate(defaultSourceDate);
      setResult(null);
    }
  }, [open, defaultSourceDate]);

  const loadGroups = useCallback(async (date) => {
    if (!date) {
      setGroups([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = await getActivityGroupsByDate(date);
      const list = Array.isArray(data) ? data : [];
      setGroups(list);
    } catch (e) {
      setError(e?.message || "Failed to load groups");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load what's already on the target day so we can skip groups that would
  // duplicate an existing one (and so a repeated copy becomes a no-op).
  const loadTargetGroups = useCallback(async (date) => {
    if (!date) {
      setTargetGroups([]);
      return;
    }
    try {
      const data = await getActivityGroupsByDate(date);
      setTargetGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      // Non-fatal: if this fails we simply don't dedupe.
      setTargetGroups([]);
    }
  }, []);

  useEffect(() => {
    if (open && sourceDate) {
      loadGroups(sourceDate);
    }
  }, [open, sourceDate, loadGroups]);

  useEffect(() => {
    if (open && targetDateStr) {
      loadTargetGroups(targetDateStr);
    }
  }, [open, targetDateStr, loadTargetGroups]);

  // Which source groups are already "covered" by the target day — count-aware,
  // not just signature-present. Several source groups can share one signature
  // (e.g. 3 identical excursion groups differing only by leader/members), so we
  // mark a source group covered only up to how many of that signature the target
  // ACTUALLY has. Copy one of three → exactly one source row is covered, the
  // other two stay ticked, and the count reads "1", not "3".
  const coveredSourceIds = useMemo(() => {
    const remaining = new Map();
    (targetGroups || []).forEach((group) => {
      const sig = groupSignature(group);
      remaining.set(sig, (remaining.get(sig) || 0) + 1);
    });

    const covered = new Set();
    (groups || []).forEach((group) => {
      const sig = groupSignature(group);
      const left = remaining.get(sig) || 0;
      if (left > 0) {
        covered.add(group.groupId);
        remaining.set(sig, left - 1);
      }
    });
    return covered;
  }, [groups, targetGroups]);

  const duplicateCount = coveredSourceIds.size;

  // Nothing is auto-selected: the user ticks exactly the groups they want to
  // copy. Start (and re-start) empty whenever a new source list loads, so an
  // accidental whole-day re-copy isn't one click away.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [groups]);

  const selectAll = () =>
    setSelectedIds(new Set((groups || []).map((group) => group.groupId)));

  const clearSelection = () => setSelectedIds(new Set());

  const toggle = (groupId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleCopy = async () => {
    if (!targetDate) return;

    // Whatever is ticked gets copied — including groups already on the target
    // day, since re-ticking one is how you deliberately create another near-
    // identical group (e.g. a second/third excursion coach). The guard is only
    // that duplicates start *unticked* (see the default-selection effect), so
    // they're never re-copied by accident.
    const idsToCopy = Array.from(selectedIds);
    if (!idsToCopy.length) return;

    setCopying(true);
    setResult(null);

    try {
      const response = await copyActivityGroups({
        targetDate,
        sourceGroupIds: idsToCopy,
        includeLeader,
        includeMembers,
      });

      const created = Array.isArray(response?.created) ? response.created : [];
      const errors = Array.isArray(response?.errors) ? response.errors : [];
      const skipped = created.reduce((sum, c) => sum + (c.membersSkipped || 0), 0);

      let message = `Copied ${created.length} group${created.length === 1 ? "" : "s"}`;
      if (includeMembers && skipped > 0) {
        message += ` · ${skipped} member${skipped === 1 ? "" : "s"} skipped (not on site)`;
      }
      if (errors.length) {
        message += ` · ${errors.length} failed`;
      }

      setResult({ message, severity: errors.length ? "warning" : "success" });
      // Clear the selection and refresh what's on the target day so a second
      // click copies nothing and the "already on" hints reflect the new state.
      setSelectedIds(new Set());
      await loadTargetGroups(targetDateStr);
      await onCopied?.();
    } catch (e) {
      setResult({ message: e?.message || "Copy failed", severity: "error" });
    } finally {
      setCopying(false);
    }
  };

  const targetLabel = targetDate
    ? dayjs(targetDate).format("ddd D MMM YYYY")
    : "this day";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Copy activity groups into {targetLabel}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Copy from"
            type="date"
            size="small"
            value={sourceDate}
            onChange={(e) => setSourceDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeLeader}
                  onChange={(e) => setIncludeLeader(e.target.checked)}
                />
              }
              label="Copy leader"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeMembers}
                  onChange={(e) => setIncludeMembers(e.target.checked)}
                />
              }
              label="Copy members"
            />
          </Stack>

          <Divider />

          {!loading && !error && groups.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" sx={{ opacity: 0.7, flexGrow: 1 }}>
                Tick the groups to copy
                {selectedIds.size > 0 ? ` · ${selectedIds.size} selected` : ""}
              </Typography>
              <Button size="small" onClick={selectAll}>
                Select all
              </Button>
              <Button
                size="small"
                onClick={clearSelection}
                disabled={selectedIds.size === 0}
              >
                Clear
              </Button>
            </Stack>
          )}

          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Loading groups…</Typography>
            </Stack>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : groups.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No activity groups on the selected day.
            </Typography>
          ) : (
            <List dense disablePadding>
              {groups.map((g) => {
                const memberCount = g.members?.length ?? 0;
                const alreadyOnTarget = coveredSourceIds.has(g.groupId);
                const detail = [
                  g.slot || "—",
                  g.campus || "—",
                  `${memberCount} member${memberCount === 1 ? "" : "s"}`,
                  g.leaderName ? g.leaderName : null,
                  alreadyOnTarget ? `already on ${targetLabel}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ");

                return (
                  <ListItem key={g.groupId} disableGutters disablePadding>
                    <ListItemButton onClick={() => toggle(g.groupId)} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          size="small"
                          checked={selectedIds.has(g.groupId)}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={g.name || "Unnamed group"}
                        secondary={detail}
                        sx={alreadyOnTarget ? { opacity: 0.6 } : undefined}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}

          {duplicateCount > 0 && (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {duplicateCount} of these {duplicateCount === 1 ? "is" : "are"} already on{" "}
              {targetLabel}. Nothing is ticked by default — tick a group to copy it
              (including one already there, to add another).
            </Typography>
          )}

          {result && <Alert severity={result.severity}>{result.message}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={copying}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleCopy}
          disabled={copying || loading || selectedIds.size === 0}
        >
          {copying
            ? "Copying…"
            : `Copy ${selectedIds.size} group${selectedIds.size === 1 ? "" : "s"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
